// La base LOCALE du navigateur (IndexedDB, via la petite bibliothèque `idb`).
// C'est elle qui rend l'application utilisable hors ligne (docs/architecture.md §4.2) :
//  - `meta`        : identifiant de l'appareil, séquence, utilisateur, dernière synchro ;
//  - `etat`        : l'état confirmé par le serveur ;
//  - `boite_envoi` : les événements créés ici, pas encore acquittés ;
//  - `contenu`     : les bundles de saison (saison courante + suivante) ;
//  - `parcours`    : les bundles des parcours annexes activés (R-PAR-01, R-OFF-02) ;
//  - `rejetes`     : les événements refusés par le serveur, gardés pour inspection (R-OFF-07).
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Etat } from '@partage/types';
import type { Evenement } from '@partage/evenements';
import type { BundleParcours, BundleSaison } from '@partage/contenu';

interface Schema extends DBSchema {
  meta: { key: string; value: unknown };
  etat: { key: string; value: Etat };
  boite_envoi: { key: string; value: Evenement; indexes: { par_date: string } };
  contenu: { key: number; value: BundleSaison };
  parcours: { key: string; value: BundleParcours };
  rejetes: { key: string; value: { evenement: Evenement; raison: string; le: string } };
}

let base: Promise<IDBPDatabase<Schema>> | undefined;

function ouvrir(): Promise<IDBPDatabase<Schema>> {
  // Version 2 : ajout du magasin `parcours` (R-PAR-01). `upgrade` est appelé depuis
  // l'ancienne version, d'où le test sur chaque magasin plutôt qu'une création en bloc.
  base ??= openDB<Schema>('formation', 2, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      if (!db.objectStoreNames.contains('etat')) db.createObjectStore('etat');
      if (!db.objectStoreNames.contains('boite_envoi')) {
        const boite = db.createObjectStore('boite_envoi', { keyPath: 'id' });
        boite.createIndex('par_date', 'cree_le');
      }
      if (!db.objectStoreNames.contains('contenu')) db.createObjectStore('contenu');
      if (!db.objectStoreNames.contains('parcours')) db.createObjectStore('parcours');
      if (!db.objectStoreNames.contains('rejetes')) db.createObjectStore('rejetes', { keyPath: 'evenement.id' });
    },
  });
  return base;
}

export const baseLocale = {
  async lireMeta<T>(cle: string): Promise<T | undefined> {
    return (await (await ouvrir()).get('meta', cle)) as T | undefined;
  },
  async ecrireMeta(cle: string, valeur: unknown): Promise<void> {
    await (await ouvrir()).put('meta', valeur, cle);
  },
  async lireEtat(): Promise<Etat | undefined> {
    return (await ouvrir()).get('etat', 'confirme');
  },
  async ecrireEtat(etat: Etat): Promise<void> {
    await (await ouvrir()).put('etat', etat, 'confirme');
  },
  async lireBoiteEnvoi(): Promise<Evenement[]> {
    return (await ouvrir()).getAllFromIndex('boite_envoi', 'par_date');
  },
  async ajouterALaBoite(ev: Evenement): Promise<void> {
    await (await ouvrir()).put('boite_envoi', ev);
  },
  async retirerDeLaBoite(ids: string[]): Promise<void> {
    const db = await ouvrir();
    const tx = db.transaction('boite_envoi', 'readwrite');
    await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);
  },
  async lireContenu(saison: number): Promise<BundleSaison | undefined> {
    return (await ouvrir()).get('contenu', saison);
  },
  async listerContenu(): Promise<BundleSaison[]> {
    return (await ouvrir()).getAll('contenu');
  },
  async ecrireContenu(bundle: BundleSaison): Promise<void> {
    await (await ouvrir()).put('contenu', bundle, bundle.numero);
  },
  async listerParcours(): Promise<BundleParcours[]> {
    return (await ouvrir()).getAll('parcours');
  },
  async ecrireParcours(bundle: BundleParcours): Promise<void> {
    await (await ouvrir()).put('parcours', bundle, bundle.id);
  },
  async ajouterRejete(evenement: Evenement, raison: string): Promise<void> {
    await (await ouvrir()).put('rejetes', { evenement, raison, le: new Date().toISOString() });
  },
  async listerRejetes() {
    return (await ouvrir()).getAll('rejetes');
  },
  async viderRejetes(): Promise<void> {
    await (await ouvrir()).clear('rejetes');
  },
  /** Efface tout (Paramètres › Données › « Effacer les données locales »). */
  async toutEffacer(): Promise<void> {
    const db = await ouvrir();
    await Promise.all((['meta', 'etat', 'boite_envoi', 'contenu', 'parcours', 'rejetes'] as const).map((s) => db.clear(s)));
  },
};
